/**
 * Global Part-Number Alias Interceptor
 * ────────────────────────────────────
 * Purpose: any time the frontend hits a backend route that includes a
 * part number in the URL path (e.g. `/api/admin/products/JIA200001P/image`
 * or `/api/task-manager/stock-by-warehouse?jp_part_no=JIC323098P`) and
 * the response is a 404 / "not found", we automatically:
 *
 *   1. Extract the JP-like token from the URL (path segment or `jp_part_no`
 *      query param).
 *   2. Call `GET /api/parts/resolve-alias/<token>` to find the canonical JP.
 *   3. Substitute the canonical for the original in the URL.
 *   4. Re-fire the original request EXACTLY ONCE.
 *   5. Cache the mapping `originalToken → canonical` for this session so
 *      future calls skip the round-trip.
 *
 * This is the single place that turns "legacy code search doesn't work
 * on page X" into a code-wide non-issue. Add new pages without thinking
 * about aliases — the interceptor takes care of every URL.
 *
 * Wiring: import once at app bootstrap (`/app/frontend/src/App.js`
 * does `import "@/utils/aliasInterceptor"`). Idempotent — multiple
 * imports won't register twice.
 */
import axios from "axios";

const API_PREFIX = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Routes the interceptor must NEVER recurse into.
const NEVER_INTERCEPT = [
  "/parts/resolve-alias/",
  "/auth/", // login / OTP shouldn't ever carry a JP
];

// JP-like token detector — matches the codes used in our catalog:
//   JPSEVxxxx / JPSUNxxxx (canonical), JID*, MISC* (legacy admin codes),
//   J<digits>P (pre-canonicalization OE+P codes), bare numerics ≥6 chars
//   (rare OE-only matches), or simple alphanumeric ≥5 chars that look
//   like a part number. We deliberately keep this conservative so we
//   don't try to resolve random unrelated path segments.
const JP_REGEX = /^(?:JPS[A-Z]{2}\d+|JI[A-Z0-9]+P?|MISC[A-Z0-9-]+|J\d{4,}P?|[A-Z]{2,4}\d{4,}[A-Z0-9-]*)$/i;

// In-memory cache for the lifetime of this tab.
const aliasCache = new Map();   // original → canonical (or `__not_found__`)
const NOT_FOUND = "__not_found__";

const looksLikeJp = (s) =>
  s && typeof s === "string" && s.length >= 4 && JP_REGEX.test(s.trim());

// Pull the JP-like value out of a URL — query param first (most common
// because `jp_part_no=` is everywhere), otherwise the last path segment
// (the encode-safe `JPSEV1282` form).
const extractJpFromUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.origin);
    const qp = u.searchParams.get("jp_part_no")
            || u.searchParams.get("part_no")
            || u.searchParams.get("part");
    if (qp && looksLikeJp(qp)) return { source: "query", key: u.searchParams.has("jp_part_no") ? "jp_part_no" : (u.searchParams.has("part_no") ? "part_no" : "part"), value: qp };
    // path segments
    const segs = u.pathname.split("/").filter(Boolean);
    for (let i = segs.length - 1; i >= 0; i--) {
      const seg = decodeURIComponent(segs[i]);
      if (looksLikeJp(seg)) return { source: "path", index: i, value: seg };
    }
  } catch { /* ignore malformed URLs */ }
  return null;
};

// Build a new URL with the JP replaced by its canonical form.
const swapJpInUrl = (url, where, canonical) => {
  try {
    const u = new URL(url, window.location.origin);
    if (where.source === "query") {
      u.searchParams.set(where.key, canonical);
    } else {
      const segs = u.pathname.split("/");
      // segs has empty first entry from leading '/'
      let pathIdx = 0;
      const nonEmptyIdx = [];
      for (let i = 0; i < segs.length; i++) {
        if (segs[i]) nonEmptyIdx.push(i);
      }
      const realIdx = nonEmptyIdx[where.index];
      if (realIdx !== undefined) {
        segs[realIdx] = encodeURIComponent(canonical);
      }
      u.pathname = segs.join("/");
    }
    // Return the same relative form axios was given when possible.
    return url.startsWith("http") ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return url;
  }
};

// Ask the backend for the canonical JP. Memoised.
const resolveAlias = async (code) => {
  if (aliasCache.has(code)) {
    const v = aliasCache.get(code);
    return v === NOT_FOUND ? null : v;
  }
  try {
    const r = await axios.get(
      `${API_PREFIX}/parts/resolve-alias/${encodeURIComponent(code)}`,
      { _skipAliasIntercept: true }
    );
    const canonical = r?.data?.found ? r.data.canonical : null;
    aliasCache.set(code, canonical || NOT_FOUND);
    return canonical;
  } catch {
    aliasCache.set(code, NOT_FOUND);
    return null;
  }
};

// Decide whether we should attempt a retry for this error.
const shouldRetry = (error) => {
  const cfg = error?.config;
  if (!cfg || cfg._aliasRetryAttempted) return false;
  if (cfg._skipAliasIntercept) return false;
  const url = cfg.url || "";
  if (NEVER_INTERCEPT.some((p) => url.includes(p))) return false;
  // Only act on responses that look like a "missing part" error. We treat
  // 404 as the primary signal but also catch the common 400 + "not found"
  // bodies the legacy backend sometimes returns.
  const status = error?.response?.status;
  if (status === 404) return true;
  if (status === 400 || status === 422) {
    const detail = String(error?.response?.data?.detail || "").toLowerCase();
    if (detail.includes("not found") || detail.includes("part not found") || detail.includes("no such part")) {
      return true;
    }
  }
  return false;
};

let installed = false;
export const installAliasInterceptor = () => {
  if (installed) return;
  installed = true;
  axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      if (!shouldRetry(error)) throw error;
      const cfg = error.config;
      const hit = extractJpFromUrl(cfg.url);
      if (!hit) throw error;

      const canonical = await resolveAlias(hit.value);
      if (!canonical || canonical === hit.value) throw error;

      // Retry exactly once with the canonical JP swapped in.
      cfg._aliasRetryAttempted = true;
      cfg.url = swapJpInUrl(cfg.url, hit, canonical);
      // Mirror the change in any explicit `params` axios was given,
      // so its own URL-builder doesn't undo us.
      if (cfg.params && typeof cfg.params === "object") {
        for (const k of ["jp_part_no", "part_no", "part"]) {
          if (cfg.params[k] && String(cfg.params[k]) === hit.value) {
            cfg.params[k] = canonical;
          }
        }
      }
      // Also patch JSON request body if it carries the JP.
      if (cfg.data && typeof cfg.data === "string") {
        try {
          const body = JSON.parse(cfg.data);
          let mutated = false;
          for (const k of ["jp_part_no", "part_no", "jpPartNo"]) {
            if (body[k] && String(body[k]) === hit.value) {
              body[k] = canonical; mutated = true;
            }
          }
          if (mutated) cfg.data = JSON.stringify(body);
        } catch { /* not JSON */ }
      }
      return axios.request(cfg);
    },
  );
};

// Auto-install at import time. Idempotent.
installAliasInterceptor();

export default installAliasInterceptor;
